/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */
import { Injectable } from '@angular/core';
import { NotificationUserModel } from '@app/models/notification-user.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { NotificationUserListService } from '@app/services/api-services/notification-users/notification-users-list.service';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ApiFilterModel } from '@app/models/api-filter.model';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class NotificationUserService extends ApiBaseService {
  private _notificationUser: NotificationUserModel;
  protected _endpoint = 'notification_users';

  constructor(
    http: HttpClient,
    authUserService: AuthUserService,
    private notificationUsersListService: NotificationUserListService
  ) {
    super(http, authUserService);
  }

  get notificationUser(): NotificationUserModel {
    return this._notificationUser;
  }

  set notificationUser(value: NotificationUserModel) {
    this._notificationUser = value;
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getNotificationUserFromListById
   *
   * : Get a NotificationUserModel based on the id of the notificationUser within the model by checking the list
   *
   * @param id of type string
   *
   * @returns NotificationUserModel: gets the notificationUser with the id that meets the query
   */
  getNotificationUserFromListByNotificationUserId(notificationId: string, userId: string): void {
    this.notificationUser = this.notificationUsersListService.getNotificationUserById(
      notificationId,
      userId
    );
    if (this.notificationUser) {
      this.dataSubject.next(this.notificationUser);
    } else {
      this.getNotificationUserByNotificationUserId(notificationId, userId);
    }
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getNotificationUserByNotificationUserId
   *
   * : Get an notificationUser based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the notificationUser with the id that meets the query
   */
  public getNotificationUserByNotificationUserId(notificationId: string, userId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: ['notification_id', 'user_id'],
      operator: ['eq', 'eq'],
      value: [notificationId, userId],
      joinOperator: 'AND',
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }
  /**
   * Warren Dowey & Ryan Duffy
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type NotificationUserModel
   *
   * @returns Void: handle a get request from the postgres DB
   */
  protected handleData(data: NotificationUserModel[]): void {
    this.notificationUser = data[0];
    super.handleData(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleInsert
   *
   * : handles the insert of a new notificationUser into the list
   *
   * @param data of type NotificationUserModel
   *
   * @returns Void: inserts a new notificationUser into the lsit
   */
  protected handleInsert(data: NotificationUserModel[]): void {
    if (data) {
      this.notificationUser = data[0];
      this.notificationUsersListService.addToList(data[0]);
    }
    super.handleInsert(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleUpdate
   *
   * : handles the update of an notificationUser in the list
   *
   * @param data of type NotificationUserModel
   *
   * @returns Void: update the list
   */
  protected handleUpdate(data: NotificationUserModel[]): void {
    this.notificationUser = data[0];
    this.notificationUsersListService.updateInList(data[0]);
    super.handleUpdate(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleDelete
   *
   * : deletes an notificationUser from the list
   *
   * @param data of type notificationUserModel
   *
   * @returns Void: deletes a notificationUser from the list
   */
  protected handleDelete(data: NotificationUserModel[]): void {
    this.notificationUsersListService.removeFromList(data[0]);
    super.handleDelete(data);
  }
}
